// Learn tab data: content API types (apps/server/src/modules/content), draft visibility,
// and a small read-through cache so lessons, phases and articles stay readable offline.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { useSession } from '@/lib/session';

// ---------------------------------------------------------------- server contract

export type Span = { text: string; bold?: true; italic?: true; href?: string };
export type CalloutKind = 'tip' | 'note' | 'see_doctor';
export type Block =
  | { type: 'heading'; level: 2 | 3; text: string }
  | { type: 'paragraph'; spans: Span[] }
  | { type: 'list'; ordered: boolean; items: Span[][] }
  | { type: 'callout'; kind: CalloutKind; spans: Span[] }
  | { type: 'image'; key: string; alt: string }
  | { type: 'steps'; items: { key: string; alt: string; spans: Span[] }[] }
  | { type: 'divider' };

export type Progress = { openedAt: string; lastOpenedAt: string; completedAt: string | null };
export type LessonSummary = {
  id: string; title: string; order: number; minutes: number; fromWeek: number | null; toWeek: number | null; draft: boolean;
  progress: Progress | null;
};
export type ChapterSummary = { id: string; title: string; summary: string | null; order: number; draft: boolean };
export type Course = {
  chapters: (ChapterSummary & { lessons: LessonSummary[] })[];
  progress: { total: number; completed: number; lastOpenedLessonId: string | null };
};
export type PracticeInfo = { kind: 'breathing' | 'grounding' | 'body' | 'movement' | 'mind'; timer: string | null };
export type TrackLesson = LessonSummary & { type: 'lesson'; practice: PracticeInfo | null };
export type TrackArticle = ArticleSummary & { type: 'parent' };
export type TrackSection = { id: string; title: string; order: number; draft: boolean; items: (TrackLesson | TrackArticle)[] };
export type Track = {
  id: string; title: string; summary: string | null; order: number; draft: boolean;
  sections: TrackSection[];
  progress: { total: number; completed: number };
};
export type Tracks = { tracks: Track[] };

export type LessonResp = {
  lesson: Omit<LessonSummary, 'progress'> & { blocks: Block[]; practice?: PracticeInfo | null };
  chapter: ChapterSummary;
  /** Set when the lesson belongs to a topic course (Sleep, Calm parent) rather than the age course. */
  track?: { id: string; title: string } | null;
  prevId: string | null;
  nextId: string | null;
  progress: Progress;
};

export type PhaseKind = 'sleep_regression' | 'growth_spurt' | 'development';
export type SleepImpact = 'low' | 'medium' | 'high';
export type PhaseSummary = {
  id: string; kind: PhaseKind; fromWeek: number; toWeek: number; title: string; summary: string; sleepImpact: SleepImpact; draft: boolean;
};
export type PhasesAll = { phases: PhaseSummary[] };
export type PhasesForBaby = {
  babyId: string;
  age: { days: number; weeks: number; corrected: boolean };
  current: (PhaseSummary & { progress: number | null })[];
  upcoming: (PhaseSummary & { startsInDays: number | null })[];
  recent: (PhaseSummary & { endedDaysAgo: number | null })[];
};
export type PhaseDetail = PhaseSummary & { blocks: Block[]; intro: Block[]; sections: { title: string; blocks: Block[] }[] };

export type Tip = { id: string; text: string; topic: 'sleep' | 'feeding' | 'development' | 'parent'; phaseId: string | null; draft: boolean };
export type TipResp = { date: string; tip: Tip | null };

export type ArticleSummary = { id: string; title: string; order: number; topic: string; summary: string | null; minutes: number | null; draft: boolean };
export type Article = ArticleSummary & { blocks: Block[] };

export type Checkin = { day: string; mood: number; energy: number | null; sleepHours: number | null; note: string | null };
export type CheckinBody = Omit<Checkin, 'day'>;

// ---------------------------------------------------------------- drafts

/**
 * Drafts are always asked for; the server decides: only test users (or a non-production server) get them,
 * everyone else gets published content only. A Home Screen app cannot be opened with a query string, so the
 * old "open once with ?drafts=1" switch left test accounts without drafts. ?drafts=0 turns them off on this phone.
 */
function draftsWanted(): boolean {
  if (import.meta.env.DEV) return true;
  try {
    const q = new URLSearchParams(location.search).get('drafts');
    if (q === '0') localStorage.setItem('bp:drafts', '0');
    if (q === '1') localStorage.removeItem('bp:drafts');
    return localStorage.getItem('bp:drafts') !== '0';
  } catch {
    return true;
  }
}
const DRAFTS = draftsWanted();

/** Content API path with the draft flag. */
export function contentPath(path: string, params: Record<string, string | undefined> = {}): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
  if (DRAFTS) q.set('drafts', '1');
  const s = q.toString();
  return `/api/content${path}${s ? `?${s}` : ''}`;
}

// ---------------------------------------------------------------- cache

type Cached<T> = { at: number; data: T };
const PREFIX = 'bp:learn:';

function readCache<T>(key: string): Cached<T> | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Cached<T>) : null;
  } catch {
    return null;
  }
}

function writeCache(key: string, data: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify({ at: Date.now(), data }));
  } catch {
    // Full or private mode: drop the oldest learn entries once, then give up quietly.
    try {
      const keys = Object.keys(localStorage).filter((k) => k.startsWith(PREFIX));
      keys.slice(0, Math.ceil(keys.length / 2)).forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(key, JSON.stringify({ at: Date.now(), data }));
    } catch { /* ignore */ }
  }
}

export function useCacheKey(path: string | null): string | null {
  const { me } = useSession();
  return path ? `${PREFIX}${me?.id ?? 'anon'}:${path}` : null;
}

type State<T> = { key: string | null; data: T | null; error: string | null; loading: boolean };

function initial<T>(key: string | null): State<T> {
  const c = key ? readCache<T>(key) : null;
  return { key, data: c?.data ?? null, error: null, loading: !!key };
}

/**
 * GET with stale-while-revalidate: the last copy shows at once (also offline), the server's answer replaces it.
 * `fresh` lets a caller skip the request when the cached copy is still good (tip of the day: same date).
 */
export function useResource<T>(path: string | null, opts: { fresh?: (c: Cached<T>) => boolean } = {}) {
  const key = useCacheKey(path);
  const [state, setState] = useState<State<T>>(() => initial<T>(key));
  const [n, setN] = useState(0);
  const fresh = useRef(opts.fresh);
  fresh.current = opts.fresh;

  let current = state;
  if (state.key !== key) {
    current = initial<T>(key);
    setState(current);
  }

  useEffect(() => {
    if (!key || !path) return;
    const cached = readCache<T>(key);
    if (cached && n === 0 && fresh.current?.(cached)) {
      setState({ key, data: cached.data, error: null, loading: false });
      return;
    }
    const ac = new AbortController();
    setState((s) => (s.key === key ? { ...s, loading: true } : s));
    api<T>(path, { signal: ac.signal })
      .then((data) => {
        writeCache(key, data);
        setState({ key, data, error: null, loading: false });
      })
      .catch((err: unknown) => {
        if ((err as Error).name === 'AbortError') return;
        const code = err instanceof ApiError ? err.code : 'generic';
        setState((s) => (s.key === key ? { ...s, error: code, loading: false } : s));
      });
    return () => ac.abort();
  }, [key, path, n]);

  const reload = useCallback(() => setN((x) => x + 1), []);
  const mutate = useCallback((fn: (d: T) => T) => {
    setState((s) => {
      if (!s.data || !s.key) return s;
      const data = fn(s.data);
      writeCache(s.key, data);
      return { ...s, data };
    });
  }, []);

  return { ...current, reload, mutate };
}

// ---------------------------------------------------------------- dates

const pad = (n: number) => String(n).padStart(2, '0');

/** YYYY-MM-DD of a Date in the device's time zone. */
export const localDay = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** YYYY-MM-DD "today" in an IANA zone (the family's), as the server computes the tip of the day. */
export function dayIn(tz?: string, d = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch {
    return localDay(d);
  }
}

/** The last `n` local days, oldest first, ending today. */
export function lastDays(n: number): string[] {
  const out: string[] = [];
  const base = new Date();
  base.setHours(12, 0, 0, 0);
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(base);
    d.setDate(base.getDate() - i);
    out.push(localDay(d));
  }
  return out;
}

export const dayDate = (day: string) => new Date(`${day}T12:00:00`);

// ---------------------------------------------------------------- phases

/** Weeks → a parent-friendly age range: "Weeks 5–7", "3–4 months", "About 18 months". */
export function ageRange(fromWeek: number, toWeek: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (toWeek < 13) return fromWeek === toWeek ? t('age.week', { n: fromWeek }) : t('age.weeks', { from: fromWeek, to: toWeek });
  const a = Math.round(fromWeek / 4.345);
  const b = Math.round(toWeek / 4.345);
  return a >= b ? t('age.aboutMonths', { n: a }) : t('age.months', { from: a, to: b });
}

/** "in 5 days", "in 3 weeks", "in about 4 months". */
export function inTime(days: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (days < 14) return t('when.days', { count: Math.max(1, days) });
  if (days < 63) return t('when.weeks', { count: Math.round(days / 7) });
  return t('when.months', { count: Math.round(days / 30.4) });
}

/** The first phase that starts after this age (any distance), with days until it starts. */
export function nextPhase(all: PhaseSummary[], ageDays: number, skip: Set<string>) {
  const next = all
    .filter((p) => p.fromWeek * 7 > ageDays && !skip.has(p.id))
    .sort((a, b) => a.fromWeek - b.fromWeek)[0];
  return next ? { phase: next, days: next.fromWeek * 7 - ageDays } : null;
}

/** "week 2 of 6" for a phase the baby is in. */
export function phaseWeek(p: PhaseSummary, ageDays: number) {
  const total = p.toWeek - p.fromWeek + 1;
  const week = Math.min(total, Math.max(1, Math.floor(ageDays / 7) - p.fromWeek + 1));
  const daysLeft = Math.max(0, (p.toWeek + 1) * 7 - ageDays);
  return { week, total, daysLeft };
}

/** Short "what you may see" lines: the items of the phase's "Signs" section (or one titled "What you may see"). */
export function phaseSigns(p: PhaseDetail): string[] {
  const s = p.sections.find((x) => /^(signs|what you may see)/i.test(x.title.trim()));
  if (!s) return [];
  const out: string[] = [];
  for (const b of s.blocks) {
    if (b.type !== 'list') continue;
    for (const it of b.items) {
      const text = it.map((sp) => sp.text).join('').trim().replace(/[.;]$/, '');
      if (text) out.push(text);
    }
  }
  return out;
}

export type RingUnit = 'weeksLeft' | 'daysLeft' | 'weeksToGo' | 'daysToGo';
export type RingData = { value: number; unit: RingUnit; frac: number };

/**
 * The progress ring of the phase card. In a phase: time left in it, filled by how far in the baby is.
 * Before one (coming up, or a calm stretch with a next phase): time to go, filled from the end of the last phase.
 * Nothing to count (a phase just ended, nothing ahead): null, the card shows its picture instead.
 */
export function ringOf(o: {
  now: PhaseSummary | null; soon: (PhaseSummary & { startsInDays: number | null }) | null; recent: PhaseSummary | null;
  next: { phase: PhaseSummary; days: number } | null; all: PhaseSummary[] | null; ageDays: number;
}): RingData | null {
  const clamp = (x: number) => Math.min(1, Math.max(0, x));
  const left = (days: number, kind: 'Left' | 'ToGo'): Pick<RingData, 'value' | 'unit'> =>
    days < 14 ? { value: Math.max(1, days), unit: `days${kind}` } : { value: Math.round(days / 7), unit: `weeks${kind}` };
  if (o.now) {
    const w = phaseWeek(o.now, o.ageDays);
    const span = w.total * 7;
    return { ...left(w.daysLeft, 'Left'), frac: clamp(1 - w.daysLeft / span) };
  }
  if (o.recent) return null;
  const to = o.soon ? { start: o.soon.fromWeek * 7, days: o.soon.startsInDays ?? o.soon.fromWeek * 7 - o.ageDays } : o.next ? { start: o.next.phase.fromWeek * 7, days: o.next.days } : null;
  if (!to || !o.all) return null;
  const lastEnd = o.all.reduce((m, p) => ((p.toWeek + 1) * 7 <= o.ageDays ? Math.max(m, (p.toWeek + 1) * 7) : m), 0);
  const span = Math.max(1, to.start - lastEnd);
  return { ...left(to.days, o.soon ? 'ToGo' : 'Left'), frac: clamp((o.ageDays - lastEnd) / span) };
}

// ---------------------------------------------------------------- course

/** Where "Continue" goes: the last opened lesson if unread, else the next unread one, else the first. */
export function continueLesson(course: Course) {
  const flat = course.chapters.flatMap((c) => c.lessons.map((l) => ({ ...l, chapter: c })));
  if (!flat.length) return null;
  const last = flat.findIndex((l) => l.id === course.progress.lastOpenedLessonId);
  if (last >= 0 && !flat[last]!.progress?.completedAt) return { lesson: flat[last]!, mode: 'continue' as const };
  const after = flat.slice(Math.max(0, last)).find((l) => !l.progress?.completedAt) ?? flat.find((l) => !l.progress?.completedAt);
  if (after) return { lesson: after, mode: last < 0 && !flat.some((l) => l.progress) ? ('start' as const) : ('next' as const) };
  return { lesson: flat[0]!, mode: 'done' as const };
}

export const flatLessons = (course: Course) => course.chapters.flatMap((c) => c.lessons);

/** Week each stage (chapter order 1..8) starts at: matches docs/course-map.md §2. */
export const STAGE_START = [0, 6, 13, 26, 39, 52, 78, 104];

/** The baby's stage: the last chapter whose start week the baby has reached. */
export function babyStageId(course: Course, weeks: number | null): string {
  const first = course.chapters[0]?.id ?? '';
  if (weeks === null) return first;
  return course.chapters.reduce((best, ch, i) => ((STAGE_START[i] ?? 0) <= weeks ? ch.id : best), first);
}

/**
 * The lesson to read next for this baby: a lesson already started, else the first unread lesson of the baby's
 * stage, else any unread lesson that fits the baby's age, else the usual next lesson in course order.
 */
export function nextForBaby(course: Course, weeks: number | null) {
  const opened = course.progress.lastOpenedLessonId;
  const flat = flatLessons(course);
  if (weeks !== null) {
    const fits = (l: (typeof flat)[number]) => !l.progress?.completedAt && (l.fromWeek === null || l.toWeek === null || (weeks >= l.fromWeek && weeks <= l.toWeek));
    const stage = course.chapters.find((ch) => ch.id === babyStageId(course, weeks));
    // A lesson the parent started is continued only when it belongs to the baby's stage.
    const started = stage?.lessons.find((l) => l.id === opened && !l.progress?.completedAt);
    if (started) return { lesson: started, mode: 'continue' as const };
    const inStage = stage?.lessons.find(fits);
    if (inStage) return { lesson: inStage, mode: 'next' as const };
    const anyFit = flat.find((l) => fits(l) && l.fromWeek !== null);
    if (anyFit) return { lesson: anyFit, mode: 'next' as const };
    return continueLesson(course);
  }
  const started = flat.find((l) => l.id === opened && !l.progress?.completedAt);
  if (started) return { lesson: started, mode: 'continue' as const };
  return continueLesson(course);
}

// ---------------------------------------------------------------- useful now (Learn home)

export type NowItem = {
  id: string; title: string; minutes: number | null; done: boolean; opened: boolean;
  /** the course it comes from: "course" or a track id */
  source: string;
  practice: boolean;
};

const fitsAge = (l: { fromWeek: number | null; toWeek: number | null }, weeks: number) =>
  l.fromWeek !== null && l.toWeek !== null && weeks >= l.fromWeek && weeks <= l.toWeek;

/**
 * Lessons from all courses that fit the baby right now: the next age-course lessons of the baby's stage (the first
 * one is nextForBaby), then unread topic-course lessons made for this age, and one calming practice that changes daily.
 */
export function usefulNow(course: Course | null, tracks: Track[], weeks: number | null, day = new Date()): NowItem[] {
  const out: NowItem[] = [];
  const seen = new Set<string>();
  const add = (l: LessonSummary, source: string, practice = false) => {
    if (seen.has(l.id)) return;
    seen.add(l.id);
    out.push({ id: l.id, title: l.title, minutes: l.minutes, done: !!l.progress?.completedAt, opened: !!l.progress, source, practice });
  };
  if (course) {
    const n = nextForBaby(course, weeks);
    if (n) add(n.lesson, 'course');
    if (weeks !== null) {
      const stage = course.chapters.find((ch) => ch.id === babyStageId(course, weeks));
      stage?.lessons.filter((l) => !l.progress?.completedAt && (l.fromWeek === null || fitsAge(l, weeks))).slice(0, 2).forEach((l) => add(l, 'course'));
    }
  }
  // Every course lesson is already counted, so topic courses only add their own lessons.
  course?.chapters.forEach((ch) => ch.lessons.forEach((l) => seen.add(l.id)));
  for (const tr of tracks) {
    const lessons = tr.sections.flatMap((s) => s.items).filter((it): it is TrackLesson => it.type === 'lesson');
    const own = lessons.filter((l) => !l.practice && !l.progress?.completedAt && weeks !== null && fitsAge(l, weeks));
    own.slice(0, tr.id === 'sleep' ? 2 : 1).forEach((l) => add(l, tr.id));
    const practices = lessons.filter((l) => l.practice);
    if (practices.length) {
      const dayN = Math.floor(day.getTime() / 86_400_000);
      add(practices[dayN % practices.length]!, tr.id, true);
    }
  }
  return out;
}
